import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Phone } from "lucide-react";
import { Reveal } from "../components/Motion";
import { DietBadges, DietLegend } from "../components/DietBadges";
import { useOrder } from "../components/OrderProvider";
import PageHeader from "../components/layout/PageHeader";
import MenuTabs from "../components/layout/MenuTabs";
import { alacarte } from "../data/menu";
import { business } from "../data/site";
import SEO from "../components/SEO";

const dietRank = (tags = []) => (tags.includes("veg") ? 0 : 1);
const priceVal = (p) => parseFloat(String(p).replace(/[^0-9.]/g, "")) || 0;
const sortItems = (items) =>
  [...items].sort((a, b) => dietRank(a.tags) - dietRank(b.tags) || priceVal(a.price) - priceVal(b.price));

export default function ALaCarte() {
  const openOrder = useOrder();
  const [active, setActive] = useState(alacarte[0].slug);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActive(entry.target.id.replace("cat-", ""));
        });
      },
      { rootMargin: "-180px 0px -65% 0px", threshold: 0 }
    );
    alacarte.forEach((g) => {
      const el = document.getElementById(`cat-${g.slug}`);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const scrollToCat = (slug) => {
    const el = document.getElementById(`cat-${slug}`);
    if (!el) return;
    const y = el.getBoundingClientRect().top + window.scrollY - 210;
    window.scrollTo({ top: y, behavior: "smooth" });
    setActive(slug);
  };

  return (
    <div data-testid="a-la-carte-page">
      <SEO
        route="menu-a-la-carte"
        fallbackTitle="À La Carte Menu | Shaan Curry House Ottawa"
        fallbackDescription="Full à la carte menu — appetisers, tandoor, curries, biryani, breads and desserts. Veg, vegan and 100% halal options."
        breadcrumbs={[
          { name: "Home", url: "/" },
          { name: "Menu", url: "/menu/a-la-carte" },
          { name: "À La Carte", url: "/menu/a-la-carte" },
        ]}
      />
      <PageHeader
        eyebrow="Our Menu"
        title="À La Carte Menu"
        subtitle="New to Indian food? Every dish has a short description so you always know what you're ordering."
      />
      <MenuTabs />

      <section className="bg-[#141414] border-t border-white/10">
        {/* legend */}
        <div className="max-w-7xl mx-auto px-5 md:px-10 pt-12 md:pt-14">
          <Reveal>
            <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl py-5 px-6 max-w-3xl mx-auto">
              <DietLegend />
            </div>
          </Reveal>
        </div>

        {/* sticky category filter */}
        <div className="sticky top-20 md:top-24 z-40 bg-[#141414]/95 backdrop-blur-xl border-y border-white/10 mt-8">
          <div className="max-w-7xl mx-auto px-3 md:px-10">
            <div data-testid="menu-filter" className="flex md:justify-center gap-2 md:gap-3 overflow-x-auto no-scrollbar py-4">
              {alacarte.map((g) => (
                <button
                  key={g.slug}
                  data-testid={`filter-${g.slug}`}
                  onClick={() => scrollToCat(g.slug)}
                  className={`shrink-0 whitespace-nowrap rounded-full px-5 py-2.5 text-base font-semibold transition-colors border-2 ${
                    active === g.slug
                      ? "bg-[#ffd100] text-[#0a0a0a] border-[#ffd100]"
                      : "bg-transparent text-[#ffd100] border-[#ffd100]/50 hover:bg-[#ffd100] hover:text-[#0a0a0a] hover:border-[#ffd100]"
                  }`}
                >
                  {g.short}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* sections */}
        <div className="max-w-7xl mx-auto px-5 md:px-10 py-14 md:py-20 space-y-16 md:space-y-20">
          {alacarte.map((group) => (
            <div key={group.slug} id={`cat-${group.slug}`} className="scroll-mt-48">
              <Reveal>
                <div className="flex flex-wrap items-center gap-3 border-b-2 border-[#ffd100]/40 pb-3 mb-3">
                  <span className="text-[#ffd100]/60 text-sm">•••</span>
                  <h3 className="font-display text-2xl md:text-3xl font-semibold text-[#ffd100]">{group.category}</h3>
                  {group.portion && (
                    <span className="text-neutral-400 text-sm font-semibold uppercase tracking-wider ml-1">{group.portion}</span>
                  )}
                </div>
                {group.note && <p className="text-neutral-500 text-base italic mb-8">{group.note}</p>}
              </Reveal>

              {group.subgroups ? (
                <div className="space-y-10">
                  {group.subgroups.map((sub) => (
                    <div key={sub.label}>
                      <div className="mb-5 flex items-center gap-3">
                        <span className="inline-flex items-center gap-2 bg-[#ffd100]/12 border border-[#ffd100]/40 rounded-full px-4 py-1.5">
                          <span className="w-1.5 h-1.5 rounded-full bg-[#ffd100]" />
                          <span className="font-display text-lg md:text-xl font-semibold text-[#ffd100] uppercase tracking-wide">{sub.label}</span>
                        </span>
                        <span className="flex-1 h-px bg-white/10" />
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-6">
                        {sortItems(sub.items).map((item) => (
                          <MenuItemRow key={item.name} item={item} />
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-6">
                  {sortItems(group.items).map((item) => (
                    <MenuItemRow key={item.name} item={item} />
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-4xl mx-auto px-5 md:px-10 py-20 md:py-24 text-center">
        <Reveal>
          <h2 className="font-display font-bold text-3xl md:text-5xl text-white tracking-tight">Ready to order?</h2>
          <p className="mt-4 text-neutral-400 text-lg">Call us to place your order, or check out our combo meals.</p>
          <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
            <a href={`tel:${business.phoneRaw}`} data-testid="alacarte-call-btn"
              className="inline-flex items-center justify-center gap-2 bg-[#ffd100] text-[#0a0a0a] font-bold text-lg px-8 py-4 rounded-full hover:bg-[#e5bc00] transition-colors">
              <Phone size={20} /> {business.phone}
            </a>
            <button type="button" onClick={openOrder}
              className="inline-flex items-center justify-center border-2 border-white/20 text-white font-bold text-lg px-8 py-4 rounded-full hover:border-[#ffd100] hover:text-[#ffd100] transition-colors">
              Order Online
            </button>
            <Link to="/menu/combos"
              className="inline-flex items-center justify-center border-2 border-white/20 text-white font-bold text-lg px-8 py-4 rounded-full hover:border-[#ffd100] hover:text-[#ffd100] transition-colors">
              Combo Meals
            </Link>
          </div>
        </Reveal>
      </section>
    </div>
  );
}

const MenuItemRow = ({ item }) => (
  <div className="flex items-start justify-between gap-4"
    data-testid={`menu-item-${item.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
    <div className="min-w-0">
      <p className="text-white text-lg md:text-xl font-medium flex items-center gap-2 flex-wrap">
        {item.name}
        <DietBadges tags={item.tags} />
      </p>
      <p className="text-neutral-400 text-base leading-relaxed mt-1">{item.desc}</p>
    </div>
    <span className="text-[#ffd100] font-bold text-lg shrink-0">{item.price}</span>
  </div>
);
